import React from "react";
import type { ApolloClient } from "@apollo/client";
import { filter, get, groupBy, isEmpty, map, size } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Carousel, Col, Form, Row } from "react-bootstrap";
import Button from "components/Button";
import IndividualDocUploader, { docTypes } from "./IndividualDocUploader";
import Alert from "components/Alert";
import SupportEmailLink from "components/SupportEmailLink";
import IconSpinner from "components/IconSpinner";

import {
  clearUploadedDocuments,
  getNeededYellowPathDocs,
  getUserState,
  submitYellowPathDocs,
} from "actions/userActions";
import { createLoadingSelector } from "store/selectors";
import { userConstants } from "actions/types";
import { ID_DOCUMENT } from "statics/docTypes";
import { scrollToTop } from "utils/dom";

import "./IndividualDocUploader.scss";
import StepInfo from "components/StepInfo";
import DisclaimerBox from "components/DisclaimerBox";
import IconHeader from "components/IconHeader";
import type { RootState } from "types/state";

const stepToLabels: Record<number, string> = {
  1: "Upload primary document.",
  2: "Upload secondary document.",
};

const ASCEND_DOC_TYPES = ["DRIVERS_LICENSE", "SSN_CARD"];

/**
 * The runtime shape of an uploaded document as stored in
 * `state.userDocuments.documents` by `persistDocument` (actions/userActions).
 * The `Document` model on `UserDocumentsState.documents` exposes `uri`/`type`,
 * but the objects placed in state actually carry
 * `docUrl`/`docType`/`subDocType`/`documentFace` — type the boundary read to
 * the real runtime keys.
 */
interface UploadedDocument {
  docUrl?: string;
  docType?: string;
  subDocType?: string;
  documentFace?: string;
  contentType?: string;
  name?: string;
}

// `docTypes` (IndividualDocUploader) is an object literal whose entries carry a
// mix of fields (some lack `numDocs`); reads here index it with dynamic string
// keys, so view it through this shared shape at each access.
type DocTypeInfo = { description: string; name: string; numDocs?: number };
const docTypesByKey = docTypes as Record<string, DocTypeInfo>;

const loadingSelectorOnSubmit = createLoadingSelector(
  userConstants.USER_SUBMIT_YELLOWPATH_DOCS
);

const loadingSelectorForDocs = createLoadingSelector(
  userConstants.USER_NEEDED_YELLOWPATH_DOCS
);

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  // user.userState is typed `object`; the state machine value lives at `.state`
  const userState = (state.user.userState as { state: string }).state;

  // Filter documents to only include allowed types
  const primaryNeededYellowPathDocs = (state.user.primaryNeededYellowPathDocs ||
    []) as string[];
  const secondaryNeededYellowPathDocs = (state.user
    .secondaryNeededYellowPathDocs || []) as string[];

  // Filter to only include allowed document types
  const filteredPrimaryNeededDocs = primaryNeededYellowPathDocs.filter((doc) =>
    ASCEND_DOC_TYPES.includes(doc)
  );

  const filteredSecondaryNeededDocs = secondaryNeededYellowPathDocs.filter(
    (doc) => ASCEND_DOC_TYPES.includes(doc)
  );

  return {
    userState,
    isFetching: loadingSelectorOnSubmit(state),
    isGettingNeededDocs: loadingSelectorForDocs(state),
    // The Document model on userDocuments.documents exposes `uri`/`type`; the
    // objects stored at runtime carry `docType`/`docUrl`/`subDocType` (see
    // UploadedDocument) — cast at this boundary so the `docType` predicate and
    // later reads typecheck against the real runtime shape.
    documents: filter(
      state.userDocuments.documents as unknown as UploadedDocument[],
      { docType: ID_DOCUMENT }
    ),
    primaryNeededYellowPathDocs: filteredPrimaryNeededDocs,
    secondaryNeededYellowPathDocs: filteredSecondaryNeededDocs,
    tags: state.user.tags,
    ownProps,
  };
};

const mapDispatchToProps = {
  submitYellowPathDocs,
  getUserState,
  getNeededYellowPathDocs,
  clearUploadedDocuments,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Declared in the original propTypes and read in render, but never supplied
  // by a parent or mapped in mapStateToProps.
  // PRESERVED BUG (conversion-only): `error` is never mapped from redux state,
  // so it is always undefined and the error <Alert> never renders.
  error?: string;
}

type Props = OwnProps & PropsFromRedux & WithApolloClient<object>;

interface State {
  submitting: boolean;
  primaryDocType: string;
  secondaryDocType: string;
  currentStep: number;
}

class IndividualDocsRequired extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      submitting: false,
      primaryDocType: "",
      secondaryDocType: "",
      currentStep: 0,
    };
  }

  componentDidMount() {
    scrollToTop();
    window.analytics.page("Individual Documents Required");
    void this.props
      .getNeededYellowPathDocs(this.props.client as ApolloClient<object>)
      .then(() => {
        window.analytics.track("Individual Documents Displayed");
      });
  }

  _handlePrimaryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      primaryDocType: e.target.value,
    });
  };

  _handleSecondaryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      secondaryDocType: e.target.value,
    });
  };

  _onSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({
      submitting: true,
    });

    const documents = map(this.props.documents, (doc) => {
      return {
        type: ID_DOCUMENT,
        url: doc.docUrl,
        contentType: doc.contentType,
        name: doc.name,
        metadata: { subType: doc.subDocType, documentFace: doc.documentFace },
      };
    });

    void this.props
      .submitYellowPathDocs(
        this.props.client as ApolloClient<object>,
        documents as Parameters<typeof submitYellowPathDocs>[1]
      )
      .then(() => {
        void this.props.getUserState(this.props.client as ApolloClient<object>);
      });
    window.analytics.track("Submitted Individual Documents Required");
  };

  _documentRequirements = () => {
    return (
      <>
        <IconHeader variant="labelHeader" headerText="Document Requirements" />
        <ul className="steps">
          <li>
            Driver&apos;s License or State ID: Clear color photos of front and
            back. Cannot be expired.
          </li>
          <li>
            Social Security Card: Clear photo with name and number visible.
          </li>
          <li>
            Clear color photos with all four corners visible; we cannot accept
            screenshots, PDF&apos;s, or scans.
          </li>
        </ul>
      </>
    );
  };

  _advanceCarousel = () => {
    this.setState({
      currentStep: this.state.currentStep + 1,
    });
  };

  docUploader = (
    docType: string,
    neededDocs: string[],
    handleChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
    showDocTypeSelector = true,
    // Call sites pass `docType && docTypesByKey[docType].numDocs === 2`, which
    // is `string | boolean` (the empty string when docType is falsy) — accept
    // the union so the value reaching IndividualDocUploader is unchanged.
    isFrontUploader?: boolean | string,
    isBackUploader?: boolean | string,
    isSecondaryUploader?: boolean | string
  ) => {
    const label = isSecondaryUploader
      ? "Upload your secondary document."
      : "Upload your primary document.";

    const filteredDocs = neededDocs.filter((doc) =>
      ASCEND_DOC_TYPES.includes(doc)
    );

    return (
      <>
        <Card>
          <Card.Header>
            <Card.Title style={{ margin: 0, paddingTop: 10 }}>
              {label}
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <article style={{ paddingBottom: 0 }}>
              {showDocTypeSelector && (
                <>
                  <Form.Group
                    as={Col}
                    sm={8}
                    controlId="formBasicSize"
                    style={{ paddingLeft: 0, paddingBottom: 20 }}
                  >
                    <Form.Select
                      name="documentType"
                      defaultValue={""}
                      value={docType}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Document Type
                      </option>
                      {map(filteredDocs, (doc) => {
                        return (
                          <option value={doc}>
                            {docTypesByKey[doc].description}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                  {docType === "SELFIE" && (
                    <Form.Label className="selfie">
                      The selfie should be an image of your face with the ID
                      uploaded above. Please make sure the ID is legible and all
                      four corners of the ID are visible.
                    </Form.Label>
                  )}
                </>
              )}
              <Row className="mb-3">
                {!!docType && (
                  <>
                    <IndividualDocUploader
                      key={docType}
                      idDocType={docType}
                      isFrontUploader={isFrontUploader as boolean}
                      isBackUploader={isBackUploader as boolean}
                    />
                  </>
                )}
              </Row>
            </article>
          </Card.Body>
        </Card>
      </>
    );
  };

  _calcContinueDisabled = () => {
    const numOfSubmittedDocsByType = groupBy(
      this.props.documents,
      "subDocType"
    );

    const numberOfPrimaryDocsSubmitted = size(
      numOfSubmittedDocsByType[this.state.primaryDocType]
    );
    const numberOfSecondaryDocsSubmitted = size(
      numOfSubmittedDocsByType[this.state.secondaryDocType]
    );

    const totalDocsSubmitted =
      numberOfPrimaryDocsSubmitted + numberOfSecondaryDocsSubmitted;

    // if each step is always a single upload then we know that the continue/submit button should only be enabled if the total docs submitted is equal to the current step + 1 (since the current step is 0 indexed)
    return totalDocsSubmitted < this.state.currentStep + 1;
  };

  _calcShowSubmitButton = () => {
    const primaryDocType = this.state.primaryDocType;
    const secondaryDocType = this.state.secondaryDocType;

    const needsSecondary = !isEmpty(this.props.secondaryNeededYellowPathDocs);
    const numOfNeededPrimaryDocs = get(
      docTypes,
      [primaryDocType, "numDocs"],
      0
    );
    // we wont know the amount of secondary docs needed but we can assume at least 1 for calculating as a default value if needsSecondary is true
    const numOfNeededSecondaryDocs = get(
      docTypes,
      [secondaryDocType, "numDocs"],
      needsSecondary ? 1 : 0
    );
    return (
      numOfNeededPrimaryDocs + numOfNeededSecondaryDocs ===
      this.state.currentStep + 1
    );
  };

  _getSteps = () => {
    const requiresSecondaryDoc = !isEmpty(
      this.props.secondaryNeededYellowPathDocs
    );

    const steps = [
      {
        index: 1,
        label: "Primary Document",
      },
    ];

    if (requiresSecondaryDoc) {
      steps.push({
        index: 2,
        label: "Secondary Document",
      });
    }

    return steps;
  };

  _getActiveflowStep = () => {
    const numOfSteps = size(this._getSteps());
    const { currentStep } = this.state;
    // if only primary doc needed we just always return 1 as the active step or if current step is 0
    if (numOfSteps === 1 || currentStep === 0) {
      return 1;
    }

    const numberOfPrimaryDocsNeeded =
      docTypesByKey[this.state.primaryDocType].numDocs;

    if (currentStep === 1 && numberOfPrimaryDocsNeeded === 2) {
      return 1;
    }

    return 2;
  };

  _resetForm = () => {
    this.setState({
      currentStep: 0,
      primaryDocType: "",
      secondaryDocType: "",
    });

    this.props.clearUploadedDocuments();
  };

  render() {
    const neededDocs = this.props.primaryNeededYellowPathDocs || [];
    const secondaryNeededDocs = this.props.secondaryNeededYellowPathDocs || [];

    const docType = this.state.primaryDocType;
    const secondaryDocType = this.state.secondaryDocType;
    const handleChange = this._handlePrimaryChange;
    const showSubmitButton = this._calcShowSubmitButton();
    const activeFlowStep = this._getActiveflowStep();
    const showResetButton = this.state.currentStep > 0;
    const showButtonSection =
      (activeFlowStep === 1 && !!docType) ||
      (activeFlowStep === 2 && !!secondaryDocType);

    const steps = this._getSteps();
    return (
      <div id={"individual-docs-required"}>
        {this.props.isGettingNeededDocs && <IconSpinner centered />}
        {!this.props.isGettingNeededDocs && (
          <Form noValidate>
            <Col sm={9} style={{ padding: 0 }}>
              <section className="page-title-wrap">
                <article className="text-cell">
                  <p className="page-title">{stepToLabels[activeFlowStep]}</p>
                  <p className="page-subtext">
                    In order to open your IRA we need some additional
                    documentation that allows us to verify your identity. If you
                    have any questions please feel free to reach out to
                    <SupportEmailLink />
                  </p>
                  {this.props.userState ===
                    "IndividualApplicationRequiresAdditionalDocumentationForAppeal" && (
                    <p>
                      <strong>Note:</strong> Before uploading any documents,
                      make sure they are not blurry and show the entire
                      document.
                    </p>
                  )}
                </article>
              </section>

              <StepInfo steps={steps} activeStepNumber={activeFlowStep} />

              <DisclaimerBox
                // @ts-expect-error DisclaimerBox types `disclaimerText` as
                // string, but the original passes a JSX fragment here; it is
                // rendered as a ReactNode child, so the value is inert.
                disclaimerText={this._documentRequirements()}
                showLink={false}
              />

              <Carousel
                style={{ marginTop: 30 }}
                interval={null}
                // @ts-expect-error react-bootstrap types `controls` as boolean;
                // the original passes null (inert — disables the controls).
                controls={null}
                touch={false} // we don't want the questions to change on swipe left/right
                activeIndex={this.state.currentStep}
                onSelect={(currentStep) => this.setState({ currentStep })}
                slide={false}
              >
                <Carousel.Item>
                  {this.docUploader(
                    docType,
                    neededDocs,
                    handleChange,
                    true,
                    docType && docTypesByKey[docType].numDocs === 2
                  )}
                </Carousel.Item>
                {docType && docTypesByKey[docType].numDocs === 2 && (
                  <Carousel.Item>
                    {this.docUploader(
                      docType,
                      neededDocs,
                      handleChange,
                      false,
                      false,
                      true
                    )}
                  </Carousel.Item>
                )}
                <Carousel.Item>
                  {this.docUploader(
                    secondaryDocType,
                    secondaryNeededDocs,
                    this._handleSecondaryChange,
                    true,
                    secondaryDocType &&
                      docTypesByKey[secondaryDocType].numDocs === 2,
                    false,
                    true
                  )}
                </Carousel.Item>
                {secondaryDocType &&
                  docTypesByKey[secondaryDocType].numDocs === 2 && (
                    <Carousel.Item>
                      {this.docUploader(
                        secondaryDocType,
                        secondaryNeededDocs,
                        this._handleSecondaryChange,
                        false,
                        false,
                        true,
                        true
                      )}
                    </Carousel.Item>
                  )}
              </Carousel>
              <div className="disclaimer-section">
                <p
                  style={{
                    fontStyle: "italic",
                    fontSize: 14,
                    paddingTop: 10,
                    paddingLeft: 4,
                    color: "#0a2540",
                  }}
                >
                  By uploading your documents to Icon, you are re-certifying
                  that you have read and understood the Icon
                  <a
                    className="modal-link"
                    href="https://iconsavingsplan.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>
                  .
                </p>
              </div>
              {showButtonSection && (
                <div className="btn-row" style={{ paddingTop: 0 }}>
                  {this.props.error && (
                    <Alert type="error" msg={this.props.error} />
                  )}
                  {showResetButton && (
                    <Button
                      type="button"
                      name="action"
                      color={"action"}
                      onClick={this._resetForm}
                      btnLabel={"Restart"}
                    />
                  )}
                  {showSubmitButton && (
                    <Button
                      name="submit"
                      withArrow={true}
                      onClick={this._onSubmit}
                      loading={this.props.isFetching}
                      disabled={this._calcContinueDisabled()}
                      btnLabel={"Submit"}
                    />
                  )}

                  {!showSubmitButton && (
                    <Button
                      name="submit"
                      disabled={this._calcContinueDisabled()}
                      withArrow={false}
                      type={"button"}
                      btnLabel="Continue"
                      onClick={this._advanceCarousel}
                    />
                  )}
                </div>
              )}
            </Col>
          </Form>
        )}
      </div>
    );
  }
}

export default connector(withApollo<Props>(IndividualDocsRequired));
